import React, { useEffect, useState } from 'react';
import { Alert, Button, Space, Steps, Descriptions, Tag, message } from 'antd';
import ResponsiveModal from '../ResponsiveModal';
import BundlePicker from './BundlePicker';
import RoamingPicker from './RoamingPicker';
import EsimGuideLink from './EsimGuideLink';
import {
  getEsimBundles,
  getRoamingProfiles,
  topUpEsim,
  type EsimBundle,
  type RoamingProfile,
  type TopUpEsimResult,
} from '../../services/adminService';
import { readableError } from '../ProvisionEsimModal';
import { balanceTypeSummary, formatValidity, roamingLabel } from '../../esim/bundleHelpers';

interface TopUpEsimModalProps {
  iccid: string;
  open: boolean;
  onClose: () => void;
  onToppedUp?: () => void;
  // The SIM's current roaming profile, preselected so a plain "add more data
  // to the same coverage" is one click.
  currentRoaming?: { group: string; id: number } | null;
}

// KAN-167: add a bundle to an existing Travel eSIM (Vodafone v2 top-up).
// Same bundle + same coverage accumulates the balance and extends validity;
// a different coverage stacks a second bundle (max three). Throttled bundles
// cannot be stacked at all, so they are greyed out.
const TopUpEsimModal: React.FC<TopUpEsimModalProps> = ({
  iccid,
  open,
  onClose,
  onToppedUp,
  currentRoaming,
}) => {
  const [step, setStep] = useState(0);
  const [bundles, setBundles] = useState<EsimBundle[]>([]);
  const [profiles, setProfiles] = useState<RoamingProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [bundleId, setBundleId] = useState<string | undefined>();
  const [bundle, setBundle] = useState<EsimBundle | undefined>();
  const [roaming, setRoaming] = useState<{ group: string; id: number } | null>(
    currentRoaming || null
  );
  const [roamingProfile, setRoamingProfile] = useState<RoamingProfile | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<TopUpEsimResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setRoaming(currentRoaming || null);
    Promise.all([getEsimBundles(), getRoamingProfiles()])
      .then(([b, p]) => {
        if (cancelled) return;
        setBundles(b.bundles.filter(x => x.family === 'travel-sim'));
        setProfiles(p.profiles);
        if (currentRoaming)
          setRoamingProfile(
            p.profiles.find(x => x.group === currentRoaming.group && x.id === currentRoaming.id)
          );
      })
      .catch(err => {
        if (!cancelled) setErrorMsg(readableError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, currentRoaming]);

  const reset = () => {
    setStep(0);
    setBundleId(undefined);
    setBundle(undefined);
    setRoaming(currentRoaming || null);
    setRoamingProfile(undefined);
    setResult(null);
    setErrorMsg(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!bundle || !roaming) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await topUpEsim(iccid, { bundleId: bundle.id, roamingControl: roaming });
      setResult(res);
      message.success('Top-up submitted');
      onToppedUp?.();
    } catch (err) {
      const detail = readableError(err);
      setErrorMsg(detail);
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const sameCoverage = !!(
    roaming &&
    currentRoaming &&
    roaming.group === currentRoaming.group &&
    roaming.id === currentRoaming.id
  );

  return (
    <ResponsiveModal
      title={`Top up eSIM ${iccid}`}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={720}
    >
      {!result && (
        <>
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message="Adds data to this eSIM at Vodafone"
            description={
              <>
                Choose a bundle and the coverage it should apply to. Adding the <b>same coverage</b>{' '}
                as the current bundle merges the data and extends the expiry to the longer of the
                two. A <b>different coverage</b> stacks a second bundle with its own balance (up to
                three). Details in the <EsimGuideLink>eSIM guide</EsimGuideLink>.
              </>
            }
          />
          {errorMsg && (
            <Alert
              style={{ marginBottom: 16 }}
              type="error"
              showIcon
              closable
              onClose={() => setErrorMsg(null)}
              message="Top-up failed"
              description={errorMsg}
            />
          )}
          <Steps
            size="small"
            current={step}
            style={{ marginBottom: 20 }}
            items={[{ title: 'Bundle' }, { title: 'Coverage' }, { title: 'Confirm' }]}
          />
          {step === 0 && (
            <BundlePicker
              bundles={bundles}
              loading={loading}
              value={bundleId}
              family="travel-sim"
              onFamilyChange={() => undefined}
              hideFamilySwitch
              disableThrottled
              onChange={(id, b) => {
                setBundleId(id);
                setBundle(b);
              }}
            />
          )}
          {step === 1 && (
            <RoamingPicker
              profiles={profiles}
              loading={loading}
              value={roaming}
              recommended={currentRoaming || bundle?.defaultRoaming || null}
              onChange={(v, p) => {
                setRoaming(v);
                setRoamingProfile(p);
              }}
            />
          )}
          {step === 2 && bundle && roaming && (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Bundle">
                  {bundle.name} — {bundle.data || '—'}, {formatValidity(bundle.validity)}
                </Descriptions.Item>
                <Descriptions.Item label="After the allowance">
                  <Tag color="blue">{bundle.balanceType.label}</Tag>{' '}
                  {balanceTypeSummary(bundle.balanceType)}
                </Descriptions.Item>
                <Descriptions.Item label="Coverage">
                  {roamingProfile
                    ? `${roamingLabel(roamingProfile)} — ${roamingProfile.group}`
                    : `Profile ${roaming.id}, ${roaming.group}`}
                </Descriptions.Item>
                <Descriptions.Item label="Effect">
                  {sameCoverage
                    ? 'Same coverage as the current bundle: the data is added to the existing balance and the expiry is extended to the later date.'
                    : 'Different coverage: a separate bundle is stacked on the eSIM with its own balance and expiry. Vodafone allows at most three.'}
                </Descriptions.Item>
              </Descriptions>
            </Space>
          )}
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Space>
              {step > 0 && <Button onClick={() => setStep(step - 1)}>Back</Button>}
              {step === 0 && (
                <Button type="primary" disabled={!bundle} onClick={() => setStep(1)}>
                  Next: coverage
                </Button>
              )}
              {step === 1 && (
                <Button type="primary" disabled={!roaming} onClick={() => setStep(2)}>
                  Next: confirm
                </Button>
              )}
              {step === 2 && (
                <Button type="primary" loading={submitting} onClick={handleSubmit}>
                  Top up
                </Button>
              )}
            </Space>
          </div>
        </>
      )}
      {result && (
        <div>
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message="Top-up submitted"
            description={`${result.bundle.name} (${result.bundle.data || ''}) was added on ${result.roaming.name || result.roaming.group}. The balance on this page refreshes within a minute.`}
          />
          <Button type="primary" onClick={handleClose}>
            Done
          </Button>
        </div>
      )}
    </ResponsiveModal>
  );
};

export default TopUpEsimModal;
